import React from 'react';
import { motion } from 'framer-motion';
import { Brain, TrendingUp, Activity, Heart } from 'lucide-react';
import { HealthMetrics } from '../../types/health';

interface StrainInsightsProps {
  metrics: HealthMetrics;
}

export function StrainInsights({ metrics }: StrainInsightsProps) {
  const insights = [
    {
      title: 'Strain Balance',
      description: 'Your strain distribution shows optimal balance between high and low intensity activities.',
      impact: 'Supports both performance gains and recovery',
      icon: <Activity className="w-5 h-5 text-primary" />,
      color: 'from-primary/20 to-purple-500/20'
    },
    {
      title: 'Recovery Impact',
      description: `With ${metrics.recovery}% recovery, your body is well-prepared for today's strain.`,
      impact: 'Maintain current intensity for optimal adaptation',
      icon: <Heart className="w-5 h-5 text-success" />,
      color: 'from-success/20 to-green-500/20'
    },
    {
      title: 'Training Adaptation',
      description: 'Your strain patterns show consistent progressive overload.',
      impact: 'Positive trend in fitness development',
      icon: <TrendingUp className="w-5 h-5 text-blue-500" />,
      color: 'from-blue-500/20 to-cyan-500/20'
    }
  ];

  return (
    <div className="theme-card p-6">
      <div className="flex items-center gap-3 mb-6">
        <Brain className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold">ML-Powered Insights</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {insights.map((insight, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative overflow-hidden rounded-lg p-4"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${insight.color}`} />
            <div className="relative space-y-3">
              <div className="flex items-center gap-2">
                {insight.icon}
                <h4 className="font-medium">{insight.title}</h4>
              </div>
              <p className="text-sm text-muted-foreground">
                {insight.description}
              </p>
              <div className="text-xs font-medium">
                Impact: <span className="text-primary">{insight.impact}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}